import {useCallback, useEffect, useMemo, useState} from 'react';
import {isFunction, keys, sortBy} from 'underscore';
import {SortDirection, TableHeaderColumnSoratbleOwnProps} from './table-header-column-sortable';

type SortableColumn<T extends Partial<Record<keyof T, unknown>>> =
	| ((itemA: T, itemB: T) => number)
	| true;

type UseSortableColumns<T extends Partial<Record<keyof T, unknown>>> = {
	data: T[];
	columns: { [key in keyof Partial<T>]: SortableColumn<T> };
	activeSortedKey?: keyof T | false;
};

export const useSortableColumns = <
	T extends Partial<Record<keyof T, unknown>>
>({
		data: initialData,
		columns,
		activeSortedKey: initialSortedKey,
	}: UseSortableColumns<T>) => {
	const [activeSortedKey, setActiveSortedKey] = useState(initialSortedKey);
	const [data, setData] = useState(initialData);
	const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.Asc);

	useEffect(() => {
		if (activeSortedKey) {
			getOnColumnClick(activeSortedKey, initialData)(sortDirection);

			return;
		}

		setData(initialData);
	}, [initialData]);

	const getOnColumnClick = useCallback(
		(columnKey: keyof T, data: T[]): TableHeaderColumnSoratbleOwnProps['onClick'] =>
			(sortDirection: SortDirection) => {
				const sortedFn = columns[columnKey];

				const sortedData: T[] = isFunction(sortedFn)
					? [...data.sort(sortedFn)]
					: sortBy(data, (item) => {
						const prop = item[columnKey];
						if(typeof prop === 'string') {
							return (prop as string).toLowerCase();
						}
						return prop;
					});

				setData(
					sortDirection === SortDirection.Asc
						? sortedData
						: sortedData.reverse()
				);

				setActiveSortedKey(columnKey);
				setSortDirection(sortDirection);
			},
		[columns]
	);

	const sortableColumns = useMemo(() => {
		const sortableColumns = {} as {
			[key in keyof Partial<T>]: TableHeaderColumnSoratbleOwnProps;
		};

		keys(columns).forEach((key) => {
			const columnKey = key as keyof T;

			sortableColumns[columnKey] = {
				onClick: getOnColumnClick(columnKey, data),
				isActive: columnKey === activeSortedKey,
			};
		});

		return sortableColumns;
	}, [activeSortedKey, columns, getOnColumnClick, data]);

	return {
		data,
		sortableColumns,
		activeSortedKey,
	};
};
